import {
  Box,
  Divider,
  FormHelperText,
  InputAdornment,
  Stack,
  SvgIconProps,
} from '@mui/material'
import { forwardRef } from 'react'
import {
  Button,
  IconButton,
  SnsIcons,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import { BaseLayout } from '@/components/layouts'
import { FullscreenModal } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { tLabel, useForm, UseFormProps, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'

export type ConsoleSNSLinkEditProps = {
  open: boolean
  onClose: () => void
  artistId: string
  type: 'instagram' | 'twitter' | 'tiktok' | 'facebook' | 'youtube'
  onSubmit: (values: SNSLinkValues) => void
}

type SNSLinkValues = {
  url: string
  type: ConsoleSNSLinkEditProps['type']
}

export const Icons: Record<
  ConsoleSNSLinkEditProps['type'],
  (props: SvgIconProps) => JSX.Element
> = {
  instagram: SnsIcons.Instagram,
  twitter: SnsIcons.Twitter,
  tiktok: SnsIcons.Tiktok,
  facebook: SnsIcons.Facebook,
  youtube: SnsIcons.Youtube,
}

const schema: UseFormProps['schema'] = o => ({
  url: o.string().uri().label(tLabel('consoleMyProfileEdit.link')),
  type: o.string().optional().allow(''),
})

export const ConsoleSNSLinkEdit: Organism<ConsoleSNSLinkEditProps> = ({
  open,
  artistId,
  onClose,
  type,
  onSubmit,
}) => {
  const { t } = useTranslation('consoleMyProfileEdit')
  const {
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    reset,
    register,
  } = useForm<SNSLinkValues>({
    schema,
    defaultValues: { url: '', type },
  })

  const onHandleSubmit = handleSubmit(values => {
    onSubmit(values)
    reset()
    onClose()
  })

  return (
    <FullscreenModal open={open} onClose={() => onClose()}>
      <ArtistThemeProvider artistId={artistId}>
        <BaseLayout
          margin
          header={{
            left: (
              <IconButton.Back
                edge="start"
                size="medium"
                onClick={onClose}
                color="white"
              />
            ),
            title: t('snsLinkEditTitle'),
          }}
        >
          <Box sx={{ py: 5 }}>
            <TextInputCell
              type="text"
              {...register('url')}
              snsType={type}
              error={errors.url}
            />
            <Stack alignItems="center" sx={{ pt: 7 }}>
              <Button
                loading={isSubmitting}
                size="large"
                sx={{ width: 244 }}
                disabled={!isValid || isSubmitting}
                onClick={onHandleSubmit}
              >
                {t('register')}
              </Button>
            </Stack>
          </Box>
        </BaseLayout>
      </ArtistThemeProvider>
    </FullscreenModal>
  )
}

const TextInputCell = forwardRef(
  (
    {
      snsType,
      error,
      ...props
    }: Omit<TextInputFieldProps, 't'> & {
      snsType: ConsoleSNSLinkEditProps['type']
    },
    ref
  ) => {
    const { t } = useTranslation('consoleMyProfileEdit')
    const Icon = Icons[snsType]
    return (
      <CellFrame>
        <TextInputField
          {...props}
          ref={ref}
          t={t}
          textFieldProps={{
            helperText: null,
            fullWidth: true,
            sx: {
              bgcolor: 'unset',
              fieldset: {
                border: 'none',
              },
              '& input:-webkit-autofill': {
                transition: 'background-color 5000s ease-in-out 0s',
              },
              '.MuiInputBase-root': { pl: 0 },
            },
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon fontSize="large" sx={{ color: 'grey.700' }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Divider color={error ? 'error.main' : 'grey.100'} />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
      </CellFrame>
    )
  }
)
